// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import './styles.css';
import { loadDataset, searchPostcodes, type Dataset } from './data';
import type { ViewId } from './types';
import type { ViewContext } from './viewContext';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossaryPopover';
import { createAbout } from './components/about';
import { createDrilldown } from './components/drilldown';
import { renderOverview } from './views/overview';
import { renderNewCar } from './views/newcar';
import { renderMap } from './views/map';
import { renderRankings } from './views/rankings';
import { renderTrends } from './views/trends';
import { renderMakes } from './views/makes';
import { renderTypes } from './views/types';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { escapeHtml, formatDate, formatPercent } from './utils/format';

// Nav tabs are words only — never count badges.
const VIEWS: Array<{ id: ViewId; label: string; render: (root: HTMLElement, ctx: ViewContext) => void }> = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'newcar', label: 'New-Car Effect', render: renderNewCar },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'rankings', label: 'Rankings', render: renderRankings },
  { id: 'trends', label: 'Trends', render: renderTrends },
  { id: 'makes', label: 'Makes & Models', render: renderMakes },
  { id: 'types', label: 'Vehicle Types', render: renderTypes },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
];

const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true">
  <path d="M4 20l1.9-5.6A2.6 2.6 0 0 1 8.35 12.6h11.3a2.6 2.6 0 0 1 2.45 1.8L24 20v3.6a1 1 0 0 1-1 1h-1.7a1 1 0 0 1-1-1v-1H7.7v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="#475569"/>
  <path d="M6.4 19l1.15-3.35A1.15 1.15 0 0 1 8.62 14.9h10.76a1.15 1.15 0 0 1 1.07.75L21.6 19z" fill="#e2e8f0"/>
  <circle cx="8.6" cy="21.1" r="1.6" fill="#0f172a"/>
  <circle cx="19.4" cy="21.1" r="1.6" fill="#0f172a"/>
  <path d="M25.8 4.5l-4 6.4h3l-.9 4.6 4.3-6.2h-2.9l1.6-4.8z" fill="#16a34a" stroke="#fff" stroke-width="0.5" stroke-linejoin="round"/>
</svg>`;

function parseHash(): { view: ViewId | null; postcode: string | null } {
  const h = location.hash.replace(/^#/, '');
  if (!h) return { view: null, postcode: null };
  if (h.startsWith('postcode=')) return { view: null, postcode: h.slice('postcode='.length) };
  const view = VIEWS.find((v) => v.id === h)?.id ?? null;
  return { view, postcode: null };
}

function shell(app: HTMLElement, data: Dataset): void {
  const { meta } = data;

  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview">${LOGO}<span>Vehicle Fleet</span><span class="brand-sub">Australia's registered vehicles</span></a>
        <div class="search-wrap">
          <input class="search-input" type="search" placeholder="Search a postcode or suburb…" aria-label="Search a postcode or suburb" autocomplete="off" />
          <div class="search-results" role="listbox"></div>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" data-role="about" aria-label="About this site" title="About this site">?</button>
      </div>
    </header>
    <nav class="nav-tabs" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>
          Data: <a href="${meta.sources.dataset}" target="_blank" rel="noopener">BITRE Road Vehicles Australia</a> ·
          boundaries ABS ASGS 2021 (CC BY 4.0). Snapshot ${escapeHtml(meta.snapshotDate)}. Updated ${formatDate(meta.generated)}.
        </span>
        <span>
          Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </span>
      </div>
    </footer>
  `;

  const viewRoot = app.querySelector('#view-root') as HTMLElement;
  const about = createAbout(meta);
  const drill = createDrilldown(data);
  let current: ViewId = 'overview';

  const ctx: ViewContext = {
    data,
    openPostcode: (pc) => drill.open(pc),
    goTo: (v) => show(v),
  };

  function show(id: ViewId, pushHash = true): void {
    const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
    drill.close();
    current = view.id;
    app.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === current)));
    viewRoot.innerHTML = '';
    try {
      view.render(viewRoot, ctx);
    } catch {
      viewRoot.innerHTML = '<div class="error-box">Something went wrong rendering this view. Try another tab.</div>';
    }
    if (pushHash) history.replaceState(null, '', `#${current}`);
    window.scrollTo({ top: 0 });
  }

  app.querySelector('.nav-inner')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) show(btn.getAttribute('data-view') as ViewId);
  });
  app.querySelector('[data-role="about"]')?.addEventListener('click', () => about.open());
  app.querySelector('.brand')?.addEventListener('click', (e) => {
    e.preventDefault();
    show('overview');
  });

  // ── Header search ──
  const input = app.querySelector('.search-input') as HTMLInputElement;
  const results = app.querySelector('.search-results') as HTMLElement;
  let hits: ReturnType<typeof searchPostcodes> = [];
  let cursor = -1;
  const closeSearch = () => {
    results.innerHTML = '';
    cursor = -1;
  };
  const paintResults = () => {
    results.innerHTML = hits
      .map((p, i) => {
        const ev = p.total > 0 ? ((p.bev + p.hybrid) / p.total) * 100 : 0;
        return `
        <button class="search-item" data-pc="${p.pc}" role="option" aria-selected="${i === cursor}">
          <strong>${escapeHtml(p.pc)}</strong>
          <span>${escapeHtml(p.loc || '—')}${p.st ? ` · ${escapeHtml(p.st)}` : ''}</span>
          <em>${formatPercent(ev)} elec.</em>
        </button>`;
      })
      .join('');
  };
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hits = searchPostcodes(data.postcodes, input.value);
      cursor = -1;
      paintResults();
    }, 300);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSearch();
      input.blur();
      return;
    }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      cursor = (cursor + 1) % hits.length;
      paintResults();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      cursor = (cursor - 1 + hits.length) % hits.length;
      paintResults();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = hits[cursor >= 0 ? cursor : 0];
      if (pick) {
        drill.open(pick.pc);
        input.value = '';
        closeSearch();
      }
    }
  });
  results.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-pc]');
    if (!btn) return;
    drill.open(btn.getAttribute('data-pc') as string);
    input.value = '';
    closeSearch();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('.search-wrap')) closeSearch();
  });

  // ── Routing ──
  const initial = parseHash();
  show(initial.view ?? 'overview', !initial.postcode);
  if (initial.postcode) drill.open(initial.postcode);
  window.addEventListener('hashchange', () => {
    const { view, postcode } = parseHash();
    if (postcode) drill.open(postcode);
    else if (view && view !== current) show(view, false);
  });
}

function renderError(app: HTMLElement, retry: () => void): void {
  app.innerHTML = `
    <main class="main-content">
      <div class="error-box">
        <strong>Could not load the data.</strong>
        <p style="margin-top:8px">Check your connection and try again.</p>
        <button type="button">Retry</button>
      </div>
    </main>`;
  app.querySelector('button')?.addEventListener('click', retry);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app') as HTMLElement;
  app.innerHTML = `
    <main class="main-content">
      <div class="loading">Loading Australia's vehicle fleet…</div>
      <div class="skeleton"></div>
    </main>`;
  initTooltip();
  initGlossary();
  try {
    const data = await loadDataset();
    shell(app, data);
  } catch {
    renderError(app, () => {
      void boot();
    });
  }
}

void boot();

export type { Dataset };
